import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Input, Select, Toast, Popconfirm, Form, Tag } from '@douyinfe/semi-ui';
import { IconEdit, IconDelete, IconPlus, IconSearch } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import ObjSelector from '../../components/ObjSelector';
import FieldValueInput from '../../components/FieldValueInput';
import styles from './index.module.scss';

const objTypeMap = {
    0: { text: '用户', type: 'primary' },
    1: { text: '员工', type: 'success' },
};

const AllFieldVals = () => {
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showBatchSetModal, setShowBatchSetModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [searchObjUid, setSearchObjUid] = useState('');
    const [searchFieldUid, setSearchFieldUid] = useState('');
    const [fieldList, setFieldList] = useState([]);
    const [formApi, setFormApi] = useState(null);
    const [batchSetFormApi, setBatchSetFormApi] = useState(null);
    const [selectedFieldUid, setSelectedFieldUid] = useState('');

    // 从URL参数中获取typ和obj_type
    const [typ, setTyp] = useState(null);
    const [obj_type, setObjType] = useState(null);

    // 从URL参数中读取typ和obj_type
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const typParam = urlParams.get('typ');
        const objTypeParam = urlParams.get('obj_type');

        // 设置默认值：typ=10, obj_type=0
        const finalTyp = typParam !== null ? parseInt(typParam) : 10;
        const finalObjType = objTypeParam !== null ? parseInt(objTypeParam) : 0;

        setTyp(finalTyp);
        setObjType(finalObjType);
    }, []);

    // 获取字段列表
    useEffect(() => {
        const fetchFieldList = async () => {
            try {
                const response = await request.get('/go/sp/all/list_all_field/', {
                    params: {
                        page: 0,
                        limit: 10,
                        status: 0, // 只获取正常状态的字段
                        typ: typ // 使用从URL获取的typ参数
                    }
                });
                if (response.errno === 0) {
                    setFieldList(response.list || []);
                } else {
                    Toast.error(response.errstr || '获取字段列表失败');
                }
            } catch (error) {
                Toast.error('获取字段列表失败');
            }
        };
        fetchFieldList();
    }, [typ]);

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/all/list_all_field_val/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    obj_type: obj_type, // 使用从URL获取的obj_type参数，固定不变
                    obj_uid: searchObjUid || undefined,
                    field_uid: searchFieldUid || undefined
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            console.error('获取列表失败:', error);
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, [obj_type]);

    const handleAdd = async (values) => {
        try {
            const response = await request.post('/go/sp/all/add_all_field_val/', values);
            if (response.errno === 0) {
                Toast.success('添加成功');
                setShowAddModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '添加失败');
            }
        } catch (error) {
            console.error('添加失败:', error);
            Toast.error('添加失败');
        }
    };

    const handleEdit = async (values) => {
        try {
            const response = await request.post('/go/sp/all/edit_all_field_val/', {
                ...values,
                uid: currentRecord.uid
            });
            if (response.errno === 0) {
                Toast.success('编辑成功');
                setShowEditModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '编辑失败');
            }
        } catch (error) {
            console.error('编辑失败:', error);
            Toast.error('编辑失败');
        }
    };

    const handleDelete = async (uids) => {
        try {
            const response = await request.post('/go/sp/all/delete_all_field_val/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids
            });
            if (response.errno === 0) {
                Toast.success('删除成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '删除失败');
            }
        } catch (error) {
            console.error('删除失败:', error);
            Toast.error('删除失败');
        }
    };

    const handleBatchSet = async (values) => {
        try {
            const vals = values.fieldValues.map(item => ({
                field_uid: item.field_uid,
                val: item.val
            }));

            const response = await request.post('/go/sp/all/set_all_field_val/', {
                obj_uid: values.obj_uid,
                vals: JSON.stringify(vals)
            });
            if (response.errno === 0) {
                Toast.success(`批量设置成功，共设置 ${response.cnt} 个字段值`);
                setShowBatchSetModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '批量设置失败');
            }
        } catch (error) {
            console.error('批量设置失败:', error);
            Toast.error('批量设置失败');
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const columns = [
        {
            title: '对象',
            dataIndex: 'obj',
            width: 200,
            render: (obj, record) => (
                <div style={{ fontSize: '12px' }}>
                    <div style={{ color: 'var(--semi-color-text-0)', marginBottom: '4px' }}>
                        {obj?.name || record.obj_uid || '-'}
                    </div>
                </div>
            )
        },
        {
            title: '字段',
            dataIndex: 'field',
            width: 200,
            render: (field) => (
                <div style={{ fontSize: '12px' }}>
                    <div style={{ color: 'var(--semi-color-text-0)', marginBottom: '4px' }}>
                        {field?.name || '-'}
                    </div>
                </div>
            )
        },
        {
            title: '值',
            dataIndex: 'val',
            width: 200,
            render: (val) => (
                <div style={{
                    maxWidth: '180px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '12px'
                }}>
                    {val || '-'}
                </div>
            )
        },
        {
            title: '创建时间/更新时间',
            dataIndex: 'update_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(record.create_time)}</div>
                    <div>{formatTimestamp(text)}</div>
                </div>
            )
        },
        {
            title: '操作',
            width: 200,
            render: (_, record) => (
                <Space>
                    <Button
                        type='primary'
                        icon={<IconEdit />}
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowEditModal(true);
                        }}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除该字段值吗？"
                        onConfirm={() => handleDelete(record.uid)}
                    >
                        <Button type='danger' icon={<IconDelete />}>删除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const renderForm = (type = 'add') => {
        const isEdit = type === 'edit';
        const initialValues = isEdit && currentRecord ? {
            field_uid: currentRecord.field_uid,
            obj_uid: currentRecord.obj_uid,
            val: currentRecord.val ?? ''
        } : {
            field_uid: '',
            obj_uid: '',
            val: ''
        };

        // 选中的字段rule
        const fieldUid = selectedFieldUid || formApi?.getValue('field_uid') || initialValues.field_uid;
        const selectedField = fieldList.find(f => f.uid === fieldUid);
        const rule = selectedField ? (selectedField.rule ? JSON.parse(selectedField.rule) : {}) : {};

        return (
            <Form
                getFormApi={setFormApi}
                initValues={initialValues}
                onSubmit={values => {
                    if (Array.isArray(values.val)) {
                        values.val = values.val.join(',');
                    }
                    if (isEdit) {
                        handleEdit(values);
                    } else {
                        handleAdd(values);
                    }
                }}
                style={{ padding: '20px' }}
            >
                <Form.Select
                    field="field_uid"
                    label="字段"
                    rules={[{ required: true, message: '请选择字段' }]}
                    placeholder="请选择字段"
                    onChange={value => {
                        // 字段切换时清空字段值
                        formApi?.setValue('val', '');
                        setSelectedFieldUid(value);
                    }}
                >
                    {fieldList.map(field => {
                        const rule = field.rule ? JSON.parse(field.rule) : {};
                        const displayName = rule.n || field.ukey || field.name || field.uid;
                        return (
                            <Select.Option key={field.uid} value={field.uid}>
                                {displayName}
                            </Select.Option>
                        );
                    })}
                </Form.Select>

                <Form.Slot label={`选择${objTypeMap[obj_type]?.text || '对象'}`}>
                    <ObjSelector
                        obj_type={obj_type}
                        value={formApi?.getValue('obj_uid')}
                        onChange={(value) => {
                            // 单选，取第一个值
                            const selectedValue = Array.isArray(value) ? value[0] : value;
                            formApi?.setValue('obj_uid', selectedValue);
                        }}
                        placeholder={`请选择${objTypeMap[obj_type]?.text || '对象'}`}
                        single={true}
                    />
                </Form.Slot>

                {/* 字段值输入，动态渲染 */}
                <FieldValueInput
                    key={fieldUid}
                    rule={rule}
                    field="val"
                    value={typeof formApi?.getValue('val') === 'string' ? formApi?.getValue('val') : (formApi?.getValue('val') ?? '')}
                    onChange={v => formApi?.setValue('val', v)}
                />
                <div style={{ marginTop: 24, textAlign: 'right' }}>
                    <Space>
                        <Button type='tertiary' onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}>取消</Button>
                        <Button type='primary' htmlType="submit">确定</Button>
                    </Space>
                </div>
            </Form>
        );
    };

    // 批量设置弹窗时初始化所有字段的 fieldValues
    useEffect(() => {
        if (showBatchSetModal && batchSetFormApi && fieldList.length > 0) {
            const initialFieldValues = fieldList.map(field => ({ field_uid: field.uid, val: '' }));
            batchSetFormApi.setValue('fieldValues', initialFieldValues);
        }
        // eslint-disable-next-line
    }, [showBatchSetModal, batchSetFormApi, fieldList]);

    const renderBatchSetForm = () => {
        return (
            <Form
                getFormApi={setBatchSetFormApi}
                initValues={{
                    obj_uid: '',
                    fieldValues: fieldList.map(field => ({ field_uid: field.uid, val: '' }))
                }}
                onSubmit={values => {
                    // 只提交有值的字段，并把数组转成字符串
                    const filteredValues = (values.fieldValues || [])
                        .filter(item => item.val !== '' && item.val !== undefined)
                        .map(item => ({
                            ...item,
                            val: Array.isArray(item.val) ? item.val.join(',') : item.val
                        }));
                    handleBatchSet({ ...values, fieldValues: filteredValues });
                }}
                style={{ padding: '20px' }}
            >
                <Form.Slot label={`选择${objTypeMap[obj_type]?.text || '对象'}`}>
                    <ObjSelector
                        obj_type={obj_type}
                        value={batchSetFormApi?.getValue('obj_uid')}
                        onChange={(value) => {
                            // 单选，取第一个值
                            const selectedValue = Array.isArray(value) ? value[0] : value;
                            batchSetFormApi?.setValue('obj_uid', selectedValue);
                        }}
                        placeholder={`请选择${objTypeMap[obj_type]?.text || '对象'}`}
                        single={true}
                    />
                </Form.Slot>

                <Form.Slot label="字段值列表">
                    <div style={{ border: '1px solid var(--semi-color-border)', borderRadius: '6px', padding: '16px' }}>
                        {fieldList.map((field, index) => {
                            const rule = field.rule ? JSON.parse(field.rule) : {};
                            const displayName = rule.n || field.ukey || field.name || field.uid;
                            return (
                                <div key={field.uid} style={{ display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'flex-end' }}>
                                    <FieldValueInput
                                        rule={rule}
                                        field={`fieldValues.${index}.val`}
                                        value={batchSetFormApi?.getValue(`fieldValues.${index}.val`)}
                                        onChange={v => batchSetFormApi?.setValue(`fieldValues.${index}.val`, v)}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </Form.Slot>
                <div style={{ marginTop: 24, textAlign: 'right' }}>
                    <Space>
                        <Button type='tertiary' onClick={() => setShowBatchSetModal(false)}>取消</Button>
                        <Button type='primary' htmlType="submit">确定</Button>
                    </Space>
                </div>
            </Form>
        );
    };

    useEffect(() => {
        if (showEditModal && currentRecord) {
            setSelectedFieldUid(currentRecord.field_uid);
        }
    }, [showEditModal, currentRecord]);

    return (
        <div className={styles.allFieldValsContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索字段值"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <ObjSelector
                        obj_type={obj_type}
                        value={searchObjUid ? searchObjUid : ''}
                        onChange={value => {
                            const selectedValue = Array.isArray(value) ? value[0] : value;
                            setSearchObjUid(selectedValue || '');
                        }}
                        placeholder={`请选择${objTypeMap[obj_type]?.text || '对象'}`}
                        single={true}
                        style={{ width: 150 }}
                    />
                    <Select
                        placeholder="字段"
                        value={searchFieldUid}
                        onChange={setSearchFieldUid}
                        style={{ width: 200 }}
                        showClear
                    >
                        <Select.Option value="">全部字段</Select.Option>
                        {fieldList.map(field => {
                            const rule = field.rule ? JSON.parse(field.rule) : {};
                            const displayName = rule.n || field.ukey || field.name || field.uid;
                            return (
                                <Select.Option key={field.uid} value={field.uid}>
                                    {displayName}
                                </Select.Option>
                            );
                        })}
                    </Select>
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                    <Button type='primary' icon={<IconPlus />} onClick={() => setShowAddModal(true)}>新增字段值</Button>
                    <Button type='secondary' icon={<IconEdit />} onClick={() => setShowBatchSetModal(true)}>批量设置</Button>
                    {selectedRowKeys.length > 0 && (
                        <Popconfirm
                            title={`确定要删除选中的 ${selectedRowKeys.length} 个字段值吗？`}
                            onConfirm={() => handleDelete(selectedRowKeys)}
                        >
                            <Button type='danger' icon={<IconDelete />}>批量删除</Button>
                        </Popconfirm>
                    )}
                </Space>
            </div>

            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys
                }}
            />

            <Modal
                title="新增字段值"
                visible={showAddModal}
                onCancel={() => setShowAddModal(false)}
                footer={null}
                centered
                width={600}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                style={{ '& ::-webkit-scrollbar': { display: 'none' } }}
            >
                {renderForm('add')}
            </Modal>

            <Modal
                title="编辑字段值"
                visible={showEditModal}
                onCancel={() => setShowEditModal(false)}
                footer={null}
                centered
                width={600}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                style={{ '& ::-webkit-scrollbar': { display: 'none' } }}
            >
                {renderForm('edit')}
            </Modal>

            <Modal
                title="批量设置字段值"
                visible={showBatchSetModal}
                onCancel={() => setShowBatchSetModal(false)}
                footer={null}
                centered
                width={600}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                style={{ '& ::-webkit-scrollbar': { display: 'none' } }}
            >
                {renderBatchSetForm()}
            </Modal>
        </div>
    );
};

export default AllFieldVals; 